import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router, Event, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs/internal/Subscription';
import { ConfirmationModalPopupComponent } from 'src/app/core/confirmation-modal-popup/confirmation-modal-popup.component';
import { HaloModalSize, HaloModalService } from 'src/app/core/halo-modal/halo-modal';
import { AmplitudeAnalyticsService } from 'src/app/core/services/amplitude-analytics.service';
import { FacilityService } from 'src/app/core/services/facility.service';
import { CommonUtil } from 'src/app/core/Utils/common-util';
import { MultiFacilityModel } from '../services/multi-facility.model';
import { MultiFacilityService } from '../services/multi-facility.service';
@Component({
  selector: 'app-multifacility-selector',
  templateUrl: './multifacility-selector.component.html',
  styleUrls: ['./multifacility-selector.component.scss'],
})
export class MultifacilitySelectorComponent implements OnInit, OnDestroy {
  facilities: MultiFacilityModel[];
  multiFacilities: MultiFacilityModel[];
  ddVisibility: boolean = false;
  searchInput: string = '';
  isPopup: boolean = false;
  checkedAll: boolean = false;
  facilityAllCount: number = 0;
  refreshRequired = false;
  selectedFacilityList:string[] = [];
  lastSelectedFacility: string;
  initialSelectedFacility:string; // Selected Facility is the initial last selected Facility;
  selectedFacilities:any;
  selectedCount: number;
  currentUrl: string;
  showConfirmUnsave = false;
  private sub = new Subscription();
  @ViewChild('action') action: TemplateRef<any>;
  @ViewChild('facilitiesTemplate') facilitiesTemplate: TemplateRef<any>;
  @ViewChild('titleTemplate') titleTemplate: TemplateRef<any>;
  @ViewChild(ConfirmationModalPopupComponent) modalComponent: ConfirmationModalPopupComponent;
  constructor(
    private _multiFacilityService: MultiFacilityService,
    private _facilityService: FacilityService,
    private _modal: HaloModalService,
    private router: Router,
    private commonUtil: CommonUtil,    
    private _amplitudeAnalytics: AmplitudeAnalyticsService
  ) {
    const routerSub = this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        if(this._multiFacilityService.isMultifacilityUrl(event.url)) {
          this.currentUrl = event.url;
          this.fetch();
          this.refreshRequired = false;
        } else if(!this.refreshRequired) {
          this.ddVisibility = false;
          this.refreshRequired = true;
          if(this.lastSelectedFacility) this._multiFacilityService.updateSelectedFacilities([this.lastSelectedFacility]);       
        }
      }
    });
    this.sub.add(routerSub);
  }

  ngOnInit(): void {
    const facilityOnChangeSub = this._facilityService.selectedFacilitySource.subscribe((res) => {
      this.initialSelectedFacility = res.facilityId;
      if(this.lastSelectedFacility != res.facilityId) {
        this.lastSelectedFacility = res.facilityId;
        this.fetch();
        this.refreshRequired = false;
        this._multiFacilityService.updateSelectedFacilities([this.lastSelectedFacility]);
      }
    });
    this.sub.add(facilityOnChangeSub);

    const refreshFacilitiesSub = this._facilityService.refreshFacilities$.subscribe(() => {
      this.fetch();
    });
    this.sub.add(refreshFacilitiesSub);
  }

  async fetch() {
    this.searchInput = '';
    let respMultiFacilities = await this._multiFacilityService.getMultiFacilities();
    respMultiFacilities = this.filter(JSON.parse(JSON.stringify(respMultiFacilities)));
    this.multiFacilities = this.selectDefault(respMultiFacilities);
    this.facilityAllCount = this.getCount(this.multiFacilities);
    this.selectedCount = this.getSelectedCount(this.multiFacilities);
    this.isPopup = this.facilityAllCount > 10;
  }

  selectDefault(facilities: MultiFacilityModel[]) {
    facilities?.forEach(facility => {
      let selectedFacility = this._facilityService.selectedMultiFacilities();
      let isValidLFODSup = this.isValidLFODSupportFacility(facility);
      if ( selectedFacility.findIndex(f => f === facility.facilityId) > -1 &&  isValidLFODSup) {
        facility.isSelected = true;
      } else {
        facility.isSelected = false;
      }
      this.selectDefault(facility.children);
    });
    return facilities;
  }

  getSelectedCount(facilities: MultiFacilityModel[]) {
    let count = 0;
    facilities?.forEach(facility => {
      if (facility?.isSelected) {
        count++;
      }
      count += this.getSelectedCount(facility.children)
    });
    return count;
  }
  

  filter(facilities: MultiFacilityModel[]) {
    let filteredFacs: MultiFacilityModel[] = [];
    facilities?.forEach(facility => {
      facility.children = this.filter(facility.children);
      facility.isEnabled = this.isValidLFODSupportFacility(facility) && 
        (facility.isAdmin || this._facilityService.getFacilityById(facility.facilityId)?.summary?.userRights.lfodEnabledEquipment !== 'H');
      if(facility.isEnabled || this.hasLFODEnabledChildren(facility)) {
        filteredFacs.push(facility);
      }
    });
    return filteredFacs;
  }

  getCount(facilities: MultiFacilityModel[]) {
    let count = 0;
    facilities?.forEach(facility => {
      if (this.isValidLFODSupportFacility(facility)) {
        count++;
      }
      count += this.getCount(facility.children)
    });
    return count;
  }

  isValidLFODSupportFacility(facilityData: MultiFacilityModel) {
    return facilityData ? this._multiFacilityService.isMultifacilitySupported(this.currentUrl??this.router.url, facilityData) : false;
  }

  hasLFODEnabledChildren(facModel: MultiFacilityModel) {
    if(!facModel || !facModel.children) return false;
    for(let child of facModel.children) {
      if(child?.isEnabled || this.hasLFODEnabledChildren(child)) return true;
    }
    return false;
  }


  updateSelection() { 
    if (this.commonUtil.rootScopeData.isPageHasUnsavedChanges) {
      this.showConfirmUnsave = true;
      // allow the current process to end before processing the showing of modal
      setTimeout(() => {            
        this.modalComponent.show(true).then(res => {
          if(res == 1) {// ignore changes
            this.commonUtil.rootScopeData.isPageHasUnsavedChanges = false; 
            if(this.lastSelectedFacility) {
              this._facilityService.selectFacility(this.lastSelectedFacility, true);
              this._amplitudeAnalytics.setUserProperty([{key: 'Facility ID', value: this.lastSelectedFacility}]);
            }
            if (this.isPopup) this._modal.close();
            else this.ddVisibility = !this.ddVisibility;
            this._multiFacilityService.updateSelectedFacilities(this.selectedFacilities);
            this.selectedFacilityList = [];//Set to empty array, selectedFacilityList was used for temporary data preparation.
          }
          this.showConfirmUnsave = false;
        });
      });
    } else {
      if(this.lastSelectedFacility) {
        this._facilityService.selectFacility(this.lastSelectedFacility, true);        
        this._amplitudeAnalytics.setUserProperty([{key: 'Facility ID', value: this.lastSelectedFacility}]);
      }
      if (this.isPopup) this._modal.close();
      else this.ddVisibility = !this.ddVisibility;
      this._multiFacilityService.updateSelectedFacilities(this.selectedFacilities);
      this.selectedFacilityList = [];//Set to empty array, selectedFacilityList was used for temporary data preparation.
    }
  }

  onShowList() {
    if (this.isPopup) {
      this._modal.add({
        dismissible: true,
        titleUppercase: true,
        modalSize: HaloModalSize.Medium,
        onDismiss: this.onDismiss,
        titleTemplate: this.titleTemplate,
        footerTemplate: this.action,
        bodyTemplate: this.facilitiesTemplate,
      });
    } else {
      this.ddVisibility = !this.ddVisibility;
    }
  }

  onDismiss() {
    //...
  }

  onCancel() {
    // Close model or Dropdown
    if (this.isPopup) {
      this._modal.close();
    } else {
      this.ddVisibility = !this.ddVisibility;
    }
    this.multiFacilities = this.selectDefault(this.multiFacilities);
    this.selectedCount = this.getSelectedCount(this.multiFacilities);
    this.lastSelectedFacility = this.initialSelectedFacility;
    this.selectedFacilityList = [];//Set to empty array, selectedFacilityList was used for temporary data preparation.
  }

  onSelectedFacilityChanged(value:any){
    let fac = value.map((val:any) => val.facilityId);
    this.selectedCount = fac.length;
    this.selectedFacilities = fac;
  }

  onLastSelectedFacilityChanged(value:any){
    if(value.isSelected){
      this.selectedFacilityList.push(value.facilityId);
    }else{
      this.selectedFacilityList = this.selectedFacilityList.filter((val:any) => val !== value.facilityId);
    }
    if(this.selectedFacilityList.length>0){
      this.lastSelectedFacility = this.selectedFacilityList[this.selectedFacilityList.length - 1];
    }else{
      this.lastSelectedFacility = this.initialSelectedFacility;
    }
  }

  onRedirectRouteChanged(value:any){
    if(value){
      if (this.isPopup) {
        this._modal.close();
      }
      this.router.navigate([value]);
    }
  }


  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

}

